import _ from 'lodash'
import T from 'prop-types'
import IT from 'react-immutable-proptypes'
import {
  List,
  fromJS,
  Iterable
} from 'immutable'
import {
  validateMacAddress,
  validateIPorCIDR,
  // validateCIDR,
  validateIP,
  validateIPv4,
  validateIPv6,
  validatePort,
  validateHash,
  // validateHashType,
  validateDomain,
  validateURL,
  // validateInt,
  // validateFloat,
  validateDateTimeString,
  getStringLengthValidator,
  getNumberRangeValidator,
  getIntRangeValidator,
  getFloatRangeValidator
} from 'pw-validators'
import {
  propTypeFloat,
  propTypeInt
} from './customPropTypes'
import {
  formatBytesToBits,
  formatBytes,
  formatNumber
} from 'pw-formatters'

const validateValueInOptions = (value, options, mustBeInOptions = true) => {
  // Verify that all values are in the option set
  const optionVal = options.find(opt => opt.get('id') === value) //_.find(options, opt => opt.id === val)
  if (mustBeInOptions && !optionVal) {
    const validOptionNames = options.map(opt => opt.get('name')).take(10).join(', ')
    return `"${value}" is not a valid option. Valid options: ${validOptionNames}${validOptionNames.size > 10 ? ', ...' : ''}`
  }
  return null
}

const enumValidator = (value, field, options = List(), mustBeInOptions = true) => {
  console.log('enumValidator', value, field, options)

  if (Iterable.isIterable(value)) {
    // Multi-value
    const maxAllowed = field.getIn(['editable', 'maxAllowed'])
    let _usedVals = List()
    let invalidMessages = value.reduce((out, val) => {
      const validationMsg = validateValueInOptions(val, options, mustBeInOptions)
      if (!validationMsg && _usedVals.includes(val)) {
        out = out.push(`"${value}" may only be used once.`)
      }
      _usedVals = _usedVals.push(val)
      return out
    }, List())

    // Validate against maximum
    if (maxAllowed && value.size > maxAllowed) {
      invalidMessages = invalidMessages.push(`Only ${maxAllowed} options may be selected.`)
    }

    return invalidMessages.size === 0 ? null : invalidMessages.join('\r\n')
  }
  else {
    // Single-value
    return validateValueInOptions(value, options, mustBeInOptions)
  }
}

const tagValidator = (inputVals, field, options) => enumValidator(inputVals, field, options, false)

const listValidator = (value = List(), field) => {
  const listItem = field.get('listItem')
  const listItemType = listItem.get('type')
  const itemValidator = listItem.getIn(['editable', 'validate'])
  const allowDuplicates = listItem.getIn(['editable', 'allowDuplicates'], false)
  console.log('~~~~Schemer ListValidator', value, field)

  let _usedValues = List()
  const validationMessages = value.reduce((out, val, idx) => {
    const itemValidatorArgs = []
    if (listItemType ===  TYPES.ENUM) {
      itemValidatorArgs.push()
    }
    const invalidMsg = itemValidator(val, listItem)
    if (invalidMsg) {
      out = out.push(`Line ${idx + 1}: ${invalidMsg}`)
    }
    if (!allowDuplicates && _usedValues.includes(val)) {
      out = out.push(`Line ${idx + 1}: Duplicate values are not allowed.`)
    }
    _usedValues = _usedValues.push(val)
    return out
  }, List())

  return validationMessages.size > 0 ? validationMessages.join(', ') : null
}

const simpleStringValidator = (value, field) => {
  const minLength = field.getIn(['editable', 'minLength'], null)
  const maxLength = field.getIn(['editable', 'maxLength'], null)
  if (maxLength || minLength) {
    return getStringLengthValidator(minLength, maxLength)(value)
  }
  return null
}

const simpleNumberValidator = (value, field) => {
  const min = field.getIn(['editable', 'min'], null)
  const max = field.getIn(['editable', 'max'], null)
  if (max || min) {
    return getIntRangeValidator(min, max)(value)
  }
  return null
}

const simpleIntValidator = (value, field) => {
  const min = field.getIn(['editable', 'min'], null)
  const max = field.getIn(['editable', 'max'], null)
  if (max || min) {
    return getNumberRangeValidator(min, max)(value)
  }
  return null
}

const simpleFloatValidator = (value, field) => {
  const min = field.getIn(['editable', 'min'], null)
  const max = field.getIn(['editable', 'max'], null)
  if (max || min) {
    return getFloatRangeValidator(min, max)(value)
  }
  return null
}

const validateHashMD5  = val => validateHash(val, 'md5')
const validateHashSHA1  = val => validateHash(val, 'sha1')
const validateHashSHA256  = val => validateHash(val, 'sha256')
const validateHashSHA512  = val => validateHash(val, 'sha512')

export const FIELD_LAYOUTS = {
  HORIZONTAL: 'HORIZONTAL',
  VERTICAL: 'VERTICAL'
}

export const FORM_STYLES = {
  STEALTH: "STEALTH",
  STANDARD: "STANDARD",
  MATERIAL: "MATERIAL"
}

export const DEFAULT_FORM_STYLE = FORM_STYLES.MATERIAL

export const ENUM_STYLES = {
  DROPDOWN: 'DROPDOWN', // Dropdown Menu OR TagInput+Typeahead if maxValues > 1
  TABS: 'TABS', // Horizontal tabs (radio:true if maxValues=1)
  LIST: 'LIST', // Selection List
  CHECKBOX_LIST: 'CHECKBOX_LIST'
}

const DEFAULT_MAX_NESTED_ITEMS_ALLOWED = 10

const ENSURE_BOOL = val => val != null ? !!val : val
const ENSURE_NUMBER = val => val != null ? +val : val
const ENSURE_TIMESTAMP = val => {
  const valAsDate = +new Date(val)
  return val && !isNaN(valAsDate) ? valAsDate : val
}
const ENSURE_STRING = val => val ? `${val}` : val



/* Schemer field types. Will drive:
* - Default editable input config
* - Default react prop-types and immutable prop-types
* - Default queryable config
*/
export const TYPE_DEFAULTS_OBJ = {
  // Special Schemer types
  // Denotes a nested editor for deep item collections with their own schemas
  SCHEMA: {
    propType: T.oneOfType([
      IT.map,
      IT.list
    ]),
    editable: {
      maxAllowed: DEFAULT_MAX_NESTED_ITEMS_ALLOWED // Max instances of this sub-collection allowed
    },
    queryable: {},
    table: {
      width: 400
    }
  },
  // Array-like list of simple values.
  LIST: {
    propType: IT.list,
    // itemType: "STRING", // Type of each list-item
    // `listItem` may contain a full sub-schema for the items in this list
    listItem: {
      type: 'STRING'
      // validate:
    },
    editable: {
      maxAllowed: DEFAULT_MAX_NESTED_ITEMS_ALLOWED, // Max list-items allowed
      validate: listValidator // Validator for entire LIST entry
      // validateItem: // Validator for each entry in this LIST. If not provided, will fall back to the `itemType`'s default validator
    },
    queryable: {},
    table: {
      width: 400
    }
  },
  ENUM: {
    // propType: T.oneOf(Object.keys(ENTITY_PRIORITY_IDS)),
    options: null,
    editable: {
      style: ENUM_STYLES.DROPDOWN,
      maxAllowed: 1,
      validate: enumValidator,
      placeholder: 'Select...'
    },
    queryable: {},
    formatDisplay: (val, field, schema) => {
      // const opts = field.get('options')
      const fieldOptions = schema.getFieldOptions(field)
      return fieldOptions && fieldOptions.hasOwnProperty(val) ? fieldOptions[val] : val
    },
    table: {
      width: 220
    },
    // formatInbound: (val, field, schema) => {
    //   // Map back to enum "ID"
    //   const fieldOptions = schema.getFieldOptions(field)
    //   const matchOption = fieldOptions.find(opt => `${opt.get('name')}`.toLowerCase() === `${val}`.toLowerCase())
    //   return matchOption ? matchOption.get('id') : null
    // }
  },
  TAGS: {
    options: null,
    editable: {
      validate: tagValidator,
      placeholder: 'Tag',
      maxAllowed: 15
    },
    queryable: {},
    table: {
      width: 200
    }
  },

  // Primitives
  BOOL: {
    propType: T.bool,
    editable: true,
    trueDisplay: "On",
    falseDisplay: "Off",
    queryable: {},
    table: {
      width: 100
    },
    formatDisplay: (val, field) => {
      return !val ? field.get('falseDisplay', 'false') : field.get('trueDisplay', 'true')
    },
    formatInbound: ENSURE_BOOL
  },
  NUMBER: {
    propType: T.number,
    editable: {
      validate: simpleNumberValidator
      // max: 100,
      // min: -100
    },
    queryable: {},
    table: {
      width: 100
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER,
    formatDisplay: val => formatNumber(val, true)
  },
  INT: {
    propType: propTypeInt,
    editable: {
      validate: simpleIntValidator
      // max: 100,
      // min: -100
    },
    queryable: {},
    table: {
      width: 100
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER,
    formatDisplay: val => formatNumber(val, true)
  },
  FLOAT: {
    propType: propTypeFloat,
    editable: {
      validate: simpleFloatValidator,
      // max: 1.2,
      // min: 1.0
    },
    queryable: {},
    table: {
      width: 100
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER,
    formatDisplay: val => formatNumber(val, true)
  },
  STRING: {
    propType: T.string,
    editable: {
      // options: [], // typeahead options (freeform string allowed)
      // minLength: 1,
      // maxLength: 100,
      validate: simpleStringValidator
    },
    queryable: {},
    table: {
      width: 200
    },
    formatInbound: ENSURE_STRING
  },
  // Self-expanding Textarea style (contentEditable/draft-js under the hood)
  STRING_LONG: {
    propType: T.string,
    editable: {
      // minLength: 10,
      // maxLength: 1000,
      validate: simpleStringValidator
    },
    queryable: {},
    table: {
      multiline: true,
      width: 200
    },
    formatInbound: ENSURE_STRING
  },


  // Units
  BYTES: {
    propType: T.number, // Always BYTES
    editable: {
      // Megabytes, megabits, etc
      unitOptions: ['B', 'kB', 'MB', 'GB', 'TB'],
      validate: _.noop, // TODO
      placeholder: "156 kB"
    },
    queryable: {},
    formatDisplay: val => val && formatBytes(val, true),
    table: {
      width: 100
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER
  },

  BYTES_PER_SEC: {
    propType: T.number,
    editable: {
      unitOptions: ['b/s', 'kb/s', 'Mb/s', 'Gb/s', 'Tb/s'], // Megabits/second, etc
      validate: _.noop, // TODO
      placeholder: "288 Mb/s"
    },
    queryable: {},
    formatDisplay: val => val && formatBytesToBits(val, true),
    table: {
      width: 100
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER
  },

  // Datetime
  DAY: {
    propType: T.string, //?
    editable: {
      validate: validateDateTimeString
    },
    queryable: {},
    table: {
      width: 200
    },
    graph: {},
    formatInbound: ENSURE_TIMESTAMP,
    // NOTE: Date/Time default formatting defaults can be changed globally via Schema Preferences
    formatDisplay: val => {
      return val && new Date(val).toLocaleString().replace(/,/g, '')
    },
  },
  TIME: {
    propType: T.string, //?
    editable: {
      validate: validateDateTimeString // FIXME improve this. More specific validation for time strings
    },
    queryable: {},
    table: {
      width: 200
    },
    graph: {},
    formatInbound: ENSURE_TIMESTAMP
  },
  DATETIME: {
    propType: propTypeInt,
    editable: {
      validate: validateDateTimeString
    },
    queryable: {},
    formatDisplay: val => {
      return val && new Date(val).toLocaleString().replace(/,/g, '')
    },
    table: {
      width: 280
    },
    graph: {},
    formatInbound: ENSURE_TIMESTAMP
  },
  DURATION_MS: { // Milliseconds
    propType: T.number, //?
    editable: {},
    queryable: {},
    table: {
      width: 200
    },
    graph: {
      graphable: true
    },
    formatInbound: ENSURE_NUMBER
  },

  // Network objects
  SUBNET: {
    propType: T.string,
    editable: {
      placeholder: "192.168.0.1/16",
      validate: validateIPorCIDR
    },
    queryable: {},
    table: {
      width: 220
    },
    formatInbound: ENSURE_STRING
  },
  IP: {
    propType: T.string,
    editable: {
      placeholder: "192.168.0.1",
      validate: validateIP
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },
  IP_V4: {
    propType: T.string,
    editable: {
      placeholder: "192.168.0.1",
      validate: validateIPv4
    },
    queryable: {},
    table: {
      width: 220
    },
    formatInbound: ENSURE_STRING
  },
  IP_V6: {
    propType: T.string,
    editable: {
      placeholder: "2001:0db8:85a3::",
      validate: validateIPv6
    },
    queryable: {},
    table: {
      width: 200
    },
    formatInbound: ENSURE_STRING
  },
  MAC_ADDRESS: {
    propType: T.string,
    editable: {
      placeholder: "01:23:45:67:89:ab",
      validate: validateMacAddress
    },
    queryable: {},
    table: {
      width: 160
    },
    formatInbound: ENSURE_STRING
  },
  PORT: {
    propType: propTypeInt,
    editable: {
      placeholder: "3000",
      validate: validatePort
    },
    queryable: {},
    table: {
      width: 100
    },
    formatInbound: ENSURE_NUMBER
  },
  DOMAIN: {
    propType: T.string,
    editable: {
      placeholder: "example.com",
      validate: validateDomain
    },
    queryable: {},
    table: {
      width: 140
    },
    formatInbound: ENSURE_STRING
  },
  URL: {
    propType: T.string,
    editable: {
      placeholder: "example.com/sample/1/route",
      validate: validateURL
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },

  // Other
  HASH: {
    propType: T.string,
    editable: {
      validate: validateHash
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },
  HASH_MD5: {
    propType: T.string,
    editable: {
      validate: validateHashMD5
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },
  HASH_SHA1: {
    propType: T.string,
    editable: {
      validate: validateHashSHA1
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },
  HASH_SHA256: {
    propType: T.string,
    editable: {
      validate: validateHashSHA256
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  },
  HASH_SHA512: {
    propType: T.string,
    editable: {
      validate: validateHashSHA512
    },
    queryable: {},
    table: {
      width: 280
    },
    formatInbound: ENSURE_STRING
  }
}

export const TYPES = _.mapValues(TYPE_DEFAULTS_OBJ, (v, k) => k) // k:k
export const TYPE_DEFAULTS = fromJS(TYPE_DEFAULTS_OBJ)
